import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import ExitConfirmationHtml from '../html/exitConfirmationPopup.html';
import Analytics from './analytics';
import Environment from './environment';
import GameFactory from './gameFactory';
import { getLogger } from './logging/logger';
import InputManager from './manager/inputManager';
import PopUpManager from './manager/popUpManager';
import MoneyBadger from './platform/comcastMoneyBadger';
import MTheory from './platform/mTheory';
import gsap from './util/hwpTVGSAP';

const logger = getLogger('Router');

const getAnchorInDescendentTree = (node) => {
	logger.log('[getAhcnorInDescendentTree]', { node });
	if (!node || node.nodeName === 'BODY') return undefined;
	if (node.nodeName === 'A') return node;
	return getAnchorInDescendentTree(node.parentNode);
};

export const HUB_EVENTS = Object.freeze({
	returnToHub: 'returnToHub',
});

class Router extends Backbone.Router {
	constructor(options) {
		super(options);
		logger.log(`[${this.constructor.name}]`);

		/** @type {function[]}  An array of functions representing custom back navigation behaviors. */
		this.backStack = [];

		/** @type {string} The document selector used when displaying pages into the dom. */
		this.selector = '#page';
	}

	initialize(options) {
		logger.log(`[${this.initialize.name}]`, { options });

		// Fix for cross sub-domain issues with Backbone (ie api.tv.hollywoodplayer.com to tv.hollywoodplayer.com)
		(() => {
			const backboneSync = Backbone.sync;
			Backbone.sync = function proxySync(method, model, syncOptions) {
				logger.log('[Proxied Sync]', { method, model, syncOptions });
				return backboneSync(method, model, {
					xhrFields: { withCredentials: true },
					...syncOptions,
					crossDomain: true,
				});
			};
		})();

		document.addEventListener('click', this.externalHrefListener.bind(this));
		InputManager.on('back', () => this.back());

		const terminationEvent = 'onpagehide' in window.self ? 'pagehide' : 'unload';
		window.addEventListener(terminationEvent, this.onTermination.bind(this));
	}

	getRunTime() {
		logger.log(`[${this.getRunTime.name}]`);

		return window.performance.now() / 1000; // Convert to seconds to keep consistent with other analytics data
	}

	/**
	 * Handler for when the page is hidden or unloaded, depending on available triggers.
	 * @link https://developer.chrome.com/blog/page-lifecycle-api/#the-unload-event
	 * @param {Event | PageTransitionEvent} event Event from the document
	 */
	onTermination(event) {
		logger.log(`[${this.onTermination.name}]`, { event });

		Analytics.sendEvent('ArcadeTermination', {
			arcadeRunTime: this.getRunTime(),
			terminationType: event.type,
		});
	}

	externalHrefListener(event) {
		logger.log(`[${this.externalHrefListener.name}]`, { event });

		const linkEl = getAnchorInDescendentTree(event.target);
		const url = linkEl?.getAttribute('href');
		if (url && !url.startsWith('#')) {
			event.preventDefault();
			const useNewWindow = !Environment.isSetTopBox();
			const targetWindow = useNewWindow ? window.open('', '_blank') : window;

			Analytics.sendEvent('ExternalLink', { url }).finally(() => {
				targetWindow.location = url;
			});
		}
	}

	registerRoute(route, name, engineModule, pageData) {
		logger.log(`[${this.registerRoute.name}]`, { route, name, pageData, engineModule });

		if (engineModule || GameFactory.supportsEngine(pageData.type)) {
			this.route(route, name, (...params) => {
				logger.debug('[onRoute]', { route, name, pageData, engineModule });

				// Initialize new options object for module
				const options = { route, ...pageData };

				// Map the route parameters to options for the View.
				const parameters = route.match(/[:*]\w+/g);
				const routeParams = params;
				if (parameters) {
					parameters.forEach((paramName, index) => {
						options[paramName.substring(1)] = routeParams[index];
					});
				}

				/**
				 * Manually send page views as described here:
				 * https://developers.google.com/analytics/devguides/collection/gtagjs/pages?hl=en#manual_pageviews
				 */
				Analytics.sendEvent('page_view', {
					page_title: name,
					page_location: window.location.href,
					page_path: window.location.pathname + window.location.hash,
				});

				Promise.resolve(engineModule || GameFactory.getEngine(pageData.type)).then(
					(Engine) => {
						if (Engine) {
							this.setCurrentPage(() => new Engine(options), name);
						} else {
							logger.error('[Failed to load engine]', {
								engineModule,
								Engine,
								pageData,
							});
						}
					},
				);
			});
		} else {
			logger.debug('[No engine available]', { pageData });
		}
	}

	setCurrentPage(page, name) {
		logger.log(`[${this.setCurrentPage.name}]`, { name, page });

		if (page) {
			const container = $(this.selector);
			const previousPage = this.currentPage;

			gsap.timeline()
				.fadeOut(container.get(0))
				.add(() => {
					// Create new page
					const newPage = page();
					this.currentPage = newPage;
					newPage.$el.attr('id', 'page').fadeTo(0, 0);

					// Replace with newest page and display
					container.replaceWith(newPage.el);
					gsap.timeline().fadeIn(newPage.el);

					// Update window title and scroll
					$('title').text(name);
					$(window).scrollTop(0);

					// Clean up old page
					previousPage?.destroy?.();
				});
		}
	}

	navigate(fragment, options) {
		logger.log(`[${this.navigate.name}]`, { fragment, options });

		const hash = window.location.hash || '#home';
		this.previousHash = hash.substring(1);

		return super.navigate(fragment, { trigger: true, ...options });
	}

	back() {
		logger.log(`[${this.back.name}]`);

		if (this.backStack.length && typeof this.backStack[0] === 'function') {
			this.backStack[0]();
			return;
		}

		if (PopUpManager.isDisplayed || !Environment.isSetTopBox()) {
			return;
		}

		if (this.isAtHome()) {
			this.exit();
		} else {
			// Just force the user back to the home page
			this.navigate('#home', {
				trigger: true,
			});
		}
	}

	/**
	 * Overrides the default back navigation behavior of the router. Multiple calls to overrideBackNavigation
	 * will be added onto a stack with the most recent taking precedence.
	 *
	 * @param {function} overrideFunction - The function to be executed when the back button is pressed.
	 */
	overrideBackNavigation(overrideFunction) {
		logger.log(`[${this.overrideBackNavigation.name}]`, { overrideFunction });

		this.backStack.unshift(overrideFunction);
	}

	/**
	 * Removes a previously set back navigation override function from the back stack.
	 *
	 * @param {function} overrideFunction - A reference to the original override function to be removed from the back handling.
	 */
	removeBackOverride(overrideFunction) {
		logger.log(`[${this.removeBackOverride.name}]`);

		this.backStack = this.backStack.filter((func) => func !== overrideFunction);
	}

	exit() {
		logger.log(`[${this.exit.name}]`);

		if (Environment.isInHub()) {
			// Ask hub to close this arcade and return to hub view
			window.parent.postMessage(HUB_EVENTS.returnToHub, document.referrer);
		} else if (Environment.isComcastSetTopBox() || Environment.isMTheory()) {
			this.promptExitConfirmation().then(
				() => {
					// User confirmed exit
					Analytics.sendEvent('ArcadeExit', {
						status: 'confirmed',
						arcadeRunTime: this.getRunTime(),
					}).finally(() => {
						// Environment dependant shutdown calls
						if (Environment.isComcastSetTopBox()) {
							MoneyBadger.shutdown();
						} else if (Environment.isMTheory()) {
							MTheory.appShutdown();
						} else {
							window.close();
						}
					});
				},
				() => {
					// User cancled exit. Return focus to application.
					Analytics.sendEvent('ArcadeExit', { status: 'Cancelled' });
					InputManager.focus();
				},
			);
		} else {
			/** Do nothing for desktop/mobile/Verizon STB. Users will have to close the tab or hit the Exit button on their remote */
		}
	}

	promptExitConfirmation() {
		logger.log(`[${this.promptExitConfirmation.name}]`);

		return new Promise((resolve, reject) => {
			PopUpManager.displayPopUp({
				contentHtml: ExitConfirmationHtml,
				events: {
					// "click button": () => PopUpManager.nextPopUp(),
					'click .btn-confirm': () => resolve(),
					'click button:not(.btn-confirm)': () => PopUpManager.nextPopUp().then(reject),
				},
			});
		});
	}

	isAtHome() {
		logger.log(`[${this.isAtHome.name}]`);

		const isAtHome =
			_.isEmpty(window.location.hash) ||
			window.location.hash.toLowerCase().replace('#', '') === 'home';
		logger.debug({ isAtHome });

		return isAtHome;
	}
}

export default new Router();
